import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  DashboardOverviewStats, 
  NodeTypeDistributionItem, 
  FeatureAdoptionItem, 
  KnowledgeBaseAdoptionStats,
  NodeEventStats,
  AllDashboardStats,
  backendApi
} from '@common/reducers/backend-api/backend-api';
import { WebRootState } from '@website/store/web-store';

// Types
export interface DashboardDateRange {
  startDate: string | null;
  endDate: string | null;
}

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export interface DashboardStatsState {
  overview: AsyncState<DashboardOverviewStats>;
  nodeTypeDistribution: AsyncState<NodeTypeDistributionItem[]>;
  featureAdoption: AsyncState<FeatureAdoptionItem[]>;
  kbAdoption: AsyncState<KnowledgeBaseAdoptionStats>;
  nodeEventStats: AsyncState<NodeEventStats>;
  dateRange: DashboardDateRange;
  lastUpdated: string | null;
}

// Initial state
const initialState: DashboardStatsState = {
  overview: {
    data: null,
    loading: false,
    error: null
  },
  nodeTypeDistribution: {
    data: null,
    loading: false,
    error: null
  },
  featureAdoption: {
    data: null,
    loading: false,
    error: null
  },
  kbAdoption: {
    data: null,
    loading: false,
    error: null
  },
  nodeEventStats: {
    data: null,
    loading: false,
    error: null
  },
  dateRange: {
    startDate: null,
    endDate: null
  },
  lastUpdated: null
};

// Async thunks
export const fetchDashboardOverviewStats = createAsyncThunk(
  'dashboardStats/fetchOverview',
  async (_: DashboardDateRange, { rejectWithValue, dispatch }) => {
    try {
      const response = await dispatch(backendApi.endpoints.getDashboardOverviewStats.initiate()).unwrap();
      return response;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch overview stats');
    }
  }
);

export const fetchNodeTypeDistribution = createAsyncThunk(
  'dashboardStats/fetchNodeTypeDistribution',
  async (_: DashboardDateRange, { rejectWithValue, dispatch }) => {
    try {
      const response = await dispatch(backendApi.endpoints.getNodeTypeDistribution.initiate()).unwrap();
      return response;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch node type distribution');
    }
  }
);

export const fetchFeatureAdoption = createAsyncThunk(
  'dashboardStats/fetchFeatureAdoption',
  async (_: DashboardDateRange, { rejectWithValue, dispatch }) => {
    try {
      const response = await dispatch(backendApi.endpoints.getAdvancedFeatureAdoption.initiate()).unwrap();
      return response;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch feature adoption stats');
    }
  }
);

export const fetchKbAdoption = createAsyncThunk(
  'dashboardStats/fetchKbAdoption',
  async (_: DashboardDateRange, { rejectWithValue, dispatch }) => {
    try {
      const response = await dispatch(backendApi.endpoints.getKnowledgeBaseAdoption.initiate()).unwrap();
      return response;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch KB adoption stats');
    }
  }
);

export const fetchAllDashboardStats = createAsyncThunk(
  'dashboardStats/fetchAll',
  async (_: DashboardDateRange, { rejectWithValue, dispatch }) => {
    try {
      const response = await dispatch(backendApi.endpoints.getAllDashboardStats.initiate()).unwrap();
      return response;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch all dashboard stats');
    }
  }
);

// Slice
const dashboardStatsSlice = createSlice({
  name: 'dashboardStats',
  initialState,
  reducers: {
    setDateRange: (state, action: PayloadAction<DashboardDateRange>) => {
      state.dateRange = action.payload;
    },
    clearDashboardStats: (state) => {
      state.overview.data = null;
      state.nodeTypeDistribution.data = null;
      state.featureAdoption.data = null;
      state.kbAdoption.data = null;
      state.nodeEventStats.data = null;
      state.lastUpdated = null;
    }
  },
  extraReducers: (builder) => {
    // Overview Stats
    builder
      .addCase(fetchDashboardOverviewStats.pending, (state) => {
        state.overview.loading = true;
        state.overview.error = null;
      })
      .addCase(fetchDashboardOverviewStats.fulfilled, (state, action: PayloadAction<DashboardOverviewStats>) => {
        state.overview.loading = false;
        state.overview.data = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDashboardOverviewStats.rejected, (state, action) => {
        state.overview.loading = false;
        state.overview.error = action.payload as string;
      });

    // Node Type Distribution
    builder
      .addCase(fetchNodeTypeDistribution.pending, (state) => {
        state.nodeTypeDistribution.loading = true;
        state.nodeTypeDistribution.error = null;
      })
      .addCase(fetchNodeTypeDistribution.fulfilled, (state, action: PayloadAction<NodeTypeDistributionItem[]>) => {
        state.nodeTypeDistribution.loading = false;
        state.nodeTypeDistribution.data = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchNodeTypeDistribution.rejected, (state, action) => {
        state.nodeTypeDistribution.loading = false;
        state.nodeTypeDistribution.error = action.payload as string;
      });

    // Feature Adoption
    builder
      .addCase(fetchFeatureAdoption.pending, (state) => {
        state.featureAdoption.loading = true;
        state.featureAdoption.error = null;
      })
      .addCase(fetchFeatureAdoption.fulfilled, (state, action: PayloadAction<FeatureAdoptionItem[]>) => {
        state.featureAdoption.loading = false;
        state.featureAdoption.data = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchFeatureAdoption.rejected, (state, action) => {
        state.featureAdoption.loading = false;
        state.featureAdoption.error = action.payload as string;
      });

    // KB Adoption
    builder
      .addCase(fetchKbAdoption.pending, (state) => {
        state.kbAdoption.loading = true;
        state.kbAdoption.error = null;
      })
      .addCase(fetchKbAdoption.fulfilled, (state, action: PayloadAction<KnowledgeBaseAdoptionStats>) => {
        state.kbAdoption.loading = false;
        state.kbAdoption.data = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchKbAdoption.rejected, (state, action) => {
        state.kbAdoption.loading = false;
        state.kbAdoption.error = action.payload as string;
      });

    // All Stats
    builder
      .addCase(fetchAllDashboardStats.pending, (state) => {
        state.overview.loading = true;
        state.nodeTypeDistribution.loading = true;
        state.featureAdoption.loading = true;
        state.kbAdoption.loading = true;
        state.nodeEventStats.loading = true;
        state.overview.error = null;
        state.nodeTypeDistribution.error = null;
        state.featureAdoption.error = null;
        state.kbAdoption.error = null;
        state.nodeEventStats.error = null;
      })
      .addCase(fetchAllDashboardStats.fulfilled, (state, action: PayloadAction<AllDashboardStats>) => {
        state.overview.loading = false;
        state.nodeTypeDistribution.loading = false;
        state.featureAdoption.loading = false;
        state.kbAdoption.loading = false;
        state.nodeEventStats.loading = false;
        state.overview.data = action.payload.overviewStats;
        state.nodeTypeDistribution.data = action.payload.nodeTypeDistribution;
        state.featureAdoption.data = action.payload.advancedFeatureAdoption;
        state.kbAdoption.data = action.payload.kbAdoption;
        state.nodeEventStats.data = action.payload.nodeEventStats;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchAllDashboardStats.rejected, (state, action) => {
        state.overview.loading = false;
        state.nodeTypeDistribution.loading = false;
        state.featureAdoption.loading = false;
        state.kbAdoption.loading = false;
        state.nodeEventStats.loading = false;
        const errorMessage = action.payload as string;
        state.overview.error = errorMessage;
        state.nodeTypeDistribution.error = errorMessage;
        state.featureAdoption.error = errorMessage;
        state.kbAdoption.error = errorMessage;
        state.nodeEventStats.error = errorMessage;
      });
  }
});

export const { setDateRange, clearDashboardStats } = dashboardStatsSlice.actions;

export const selectDashboardStats = (state: WebRootState) => state.dashboardStats;
export const selectDateRange = (state: WebRootState) => state.dashboardStats.dateRange;
export const selectLastUpdated = (state: WebRootState) => state.dashboardStats.lastUpdated;

export default dashboardStatsSlice.reducer; 